<script setup>
import { useBiDataFormatter } from '~/bi/components/widgets/table-widget/composables/bi-table-data-formatter.composable.js';

const props = defineProps({
  data: {
    type: Object,
    default: () => {},
    required: true,
  },
  config: {
    type: Object,
    default: () => {},
    required: true,
  },
  isPreview: {
    type: Boolean,
    default: false,
    required: true,
  },
});

const { formatNumber } = useBiDataFormatter();

const first_row_data = computed(() => props.data[0] || {});

const primary_number = computed(() => {
  return first_row_data.value[props.config.chart.layout_category];
});

const comparisons_list = computed(() => {
  return props.config.chart.layout_values?.map?.(item => item.value);
});

function getComparisonValue(column) {
  return first_row_data.value[column];
}

function getPercentageChange(value1, value2) {
  value1 = Number(value1);
  value2 = Number(value2);
  if (value1 === 0 && value2 === 0)
    return 0;
  return ((value1 - value2) / value2) * 100;
}

function getChange(value1, value2) {
  if (props.config.chart.compare_by === 'difference')
    return value1 - value2;
  else if (props.config.chart.compare_by === 'percentage')
    return getPercentageChange(value1, value2);
}

function getDifferenceColors(percentage) {
  if (props.config.chart.switch_positive_negative_colors) {
    if (percentage > 0)
      return 'text-red-600';
    else if (percentage < 0)
      return 'text-green-600';
    else
      return '';
  }
  if (percentage > 0)
    return 'text-green-600';
  else if (percentage < 0)
    return 'text-red-600';
  else
    return '';
}
</script>

<template>
  <div v-if="props.data.length" class="h-full">
    <div class="h-full flex flex-col justify-center gap-y-1">
      <div class="text-3xl font-semibold" :style="`color: ${props.config.chart.number_widget_foreground_color}`">
        <template v-if="primary_number < 0">
          -
        </template>{{ props.config.chart.prefix }}{{ formatNumber(Math.abs(primary_number), props.config.chart.compact) }}{{ props.config.chart.suffix }}
      </div>
      <div
        v-for="comparison in comparisons_list"
        :key="comparison"
        class="text-xs font-medium text-gray-600 flex items-center gap-1"
      >
        <span v-if="props.config.chart.compare_by !== 'none'" class="flex items-center gap-1">
          <span
            :class="getDifferenceColors(getChange(primary_number, getComparisonValue(comparison)))"
            class="flex items-center gap-0.5"
          >
            <span>
              <template v-if="getChange(primary_number, getComparisonValue(comparison)) > 0">+</template><template v-else-if="getChange(primary_number, getComparisonValue(comparison)) < 0">-</template><template v-if="props.config.chart.compare_by === 'difference'">{{ props.config.chart.prefix }}</template>{{ formatNumber(Math.abs(getChange(primary_number, getComparisonValue(comparison))), props.config.chart.compare_by === 'difference' && props.config.chart.compact) }}<template v-if="props.config.chart.compare_by === 'percentage'">%</template><template v-else-if="props.config.chart.compare_by === 'difference'">{{ props.config.chart.suffix }}</template>
            </span>
          </span>
          <span :style="`color: ${props.config.chart.number_widget_foreground_color}`">vs.</span>
        </span>
        <span :style="`color: ${props.config.chart.number_widget_foreground_color}`">{{ comparison }}: <template v-if="getComparisonValue(comparison) < 0">-</template>{{ props.config.chart.prefix }}{{ formatNumber(Math.abs(getComparisonValue(comparison)), props.config.chart.compact) }}{{ props.config.chart.suffix }}</span>
      </div>
    </div>
  </div>
  <div v-else class="h-full w-full flex flex-col justify-center items-center text-center">
    <div class="text-sm font-semibold text-gray-900 mt-4 mb-1">
      {{ $t('No data to display') }}
    </div>
  </div>
</template>
